import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const POLL_MS = 30000;

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const containerRef = useRef(null);

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  }), []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/notifications/unread-count`, { headers: authHeaders() });
      setUnreadCount(res.data.count || 0);
    } catch {
      // silent -- a failed poll shouldn't surface an error to the whole shell
    }
  }, [authHeaders]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/notifications`, { headers: authHeaders() });
      setNotifications(res.data || []);
    } catch {
      setNotifications([]);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchNotifications();
  };

  const handleNotificationClick = async (n) => {
    if (!n.read_at) {
      try {
        await axios.patch(`${API_BASE}/notifications/${n.id}/read`, {}, { headers: authHeaders() });
        setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        // ignore -- clicking through still works even if the read-marking call fails
      }
    }
    if (n.related_table && n.related_record_id) {
      setOpen(false);
      navigate(`/dashboard/${n.related_table}/form_view/${n.related_record_id}`);
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div ref={containerRef} style={styles.container}>
      <button type="button" onClick={toggleOpen} style={styles.bellButton} aria-label="Notifications">
        <Bell size={18} color="#e5e7eb" />
        {unreadCount > 0 && (
          <span style={styles.badge}>{unreadCount > 9 ? "9+" : unreadCount}</span>
        )}
      </button>

      {open && (
        <div style={styles.dropdown}>
          <div style={styles.dropdownHeader}>Notifications</div>
          {notifications.length === 0 ? (
            <div style={styles.empty}>Nothing yet.</div>
          ) : (
            notifications.map((n) => (
              <div
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                style={{ ...styles.item, ...(n.read_at ? {} : styles.itemUnread) }}
              >
                <div style={styles.itemSubject}>{n.subject}</div>
                {n.body && <div style={styles.itemBody}>{n.body}</div>}
                <div style={styles.itemTime}>{timeAgo(n.created_at)}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { position: "relative" },
  bellButton: {
    position: "relative",
    display: "grid",
    placeItems: "center",
    width: 34,
    height: 34,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    cursor: "pointer",
  },
  badge: {
    position: "absolute",
    top: -4,
    right: -4,
    background: "#ef4444",
    color: "white",
    fontSize: 10,
    fontWeight: 800,
    borderRadius: 999,
    minWidth: 16,
    height: 16,
    display: "grid",
    placeItems: "center",
    padding: "0 3px",
  },
  dropdown: {
    position: "absolute",
    top: 42,
    right: 0,
    width: 320,
    maxHeight: 400,
    overflowY: "auto",
    background: "#161d2e",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10,
    boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
    zIndex: 1100,
  },
  dropdownHeader: {
    padding: "10px 14px",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: ".04em",
    textTransform: "uppercase",
    color: "rgba(229,231,235,.6)",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  empty: { padding: "20px 14px", fontSize: 13, color: "rgba(229,231,235,.5)" },
  item: {
    padding: "10px 14px",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    cursor: "pointer",
  },
  itemUnread: { background: "rgba(59,130,246,0.08)" },
  itemSubject: { fontSize: 13, fontWeight: 700, color: "#e5e7eb" },
  itemBody: { fontSize: 12, color: "rgba(229,231,235,.7)", marginTop: 2 },
  itemTime: { fontSize: 11, color: "rgba(229,231,235,.45)", marginTop: 4 },
};

export default NotificationBell;
